import { SearchItemType } from '@lightdash/common';
import {
    IconChartBar,
    IconCode,
    IconFolders,
    IconHome,
    IconLayoutDashboard,
    IconMessageCircle,
    IconRoute,
    IconSearch,
    IconSparkles,
    IconTable,
    type Icon,
} from '@tabler/icons-react';
import { useMemo } from 'react';
import { INTELLIGENCE_PAGES } from '../../../components/NavBar/intelligencePages';
import { type SearchItem } from '../types/searchItem';
import { hasMinQueryLength } from './useSearch';

type Feature = { title: string; path: string; icon: Icon; context: string };

/** Lightbox pages matching `query`, gated the same way as the sidebar. */
export const useOmnibarFeatureItems = ({
    query,
    projectUuid,
    canManageProject,
    isAiAgentsEnabled,
    showCustomerIntelligence,
}: {
    query: string;
    projectUuid: string;
    canManageProject: boolean;
    isAiAgentsEnabled: boolean;
    showCustomerIntelligence: boolean;
}): SearchItem[] =>
    useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!hasMinQueryLength(term)) return [];
        const base = `/projects/${projectUuid}`;
        const features: Feature[] = [
            {
                title: 'Ask',
                path: `${base}/ask`,
                icon: IconMessageCircle,
                context: 'Quick cited answers',
            },
            {
                title: 'Search',
                path: `${base}/search`,
                icon: IconSearch,
                context: 'Search Lightbox and connected apps',
            },
            ...(isAiAgentsEnabled
                ? [
                      {
                          title: 'Ask AI',
                          path: `${base}/ai-agents`,
                          icon: IconSparkles,
                          context: 'AI agents',
                      },
                  ]
                : []),
            {
                title: 'Home',
                path: `${base}/home`,
                icon: IconHome,
                context: 'Project home',
            },
            ...(showCustomerIntelligence ? INTELLIGENCE_PAGES : []).map(
                ([title, path]) => ({
                    title,
                    path: `${base}/intelligence/${path}`,
                    icon: IconRoute,
                    context: 'Customer intelligence',
                }),
            ),
            ...(canManageProject
                ? [
                      {
                          title: 'Workflows',
                          path: `${base}/workflows`,
                          icon: IconRoute,
                          context: 'Work intelligence',
                      },
                  ]
                : []),
            {
                title: 'Explore',
                path: `${base}/tables`,
                icon: IconTable,
                context: 'Data intelligence',
            },
            {
                title: 'SQL runner',
                path: `${base}/sql-runner`,
                icon: IconCode,
                context: 'Data intelligence',
            },
            {
                title: 'Dashboards',
                path: `${base}/dashboards`,
                icon: IconLayoutDashboard,
                context: 'Content',
            },
            {
                title: 'Charts',
                path: `${base}/saved`,
                icon: IconChartBar,
                context: 'Content',
            },
            {
                title: 'Spaces',
                path: `${base}/spaces`,
                icon: IconFolders,
                context: 'Content',
            },
        ];
        return features
            .filter(
                (feature) =>
                    feature.title.toLowerCase().includes(term) ||
                    feature.context.toLowerCase().includes(term),
            )
            .map<SearchItem>((feature) => ({
                type: SearchItemType.PAGE,
                title: feature.title,
                icon: feature.icon,
                contextLabel: feature.context,
                location: { pathname: feature.path },
            }));
    }, [
        query,
        projectUuid,
        canManageProject,
        isAiAgentsEnabled,
        showCustomerIntelligence,
    ]);
